import { AlertTriangle, RefreshCw } from 'lucide-react';
import { isChunkLoadError } from '@/utils/lazyWithRetry';

interface FeatureErrorFallbackProps {
  featureName: string;
  error: Error;
  onReset: () => void;
}

/**
 * Lightweight error fallback for feature-level error boundaries.
 * Shows inline in the feature area instead of replacing the whole app.
 *
 * For chunk load errors (stale deployments), shows a "Reload Page" button
 * instead of "Try Again" since re-rendering won't fix missing assets.
 */
export function FeatureErrorFallback({ featureName, error, onReset }: FeatureErrorFallbackProps) {
  const isChunkError = isChunkLoadError(error);

  return (
    <div className="flex flex-col items-center justify-center p-8 text-center min-h-[200px]">
      <AlertTriangle size={32} className="text-amber-500 mb-3" />
      <h3 className="text-lg font-semibold text-slate-800 mb-1">
        {isChunkError ? 'A newer version is available' : `${featureName} encountered an error`}
      </h3>
      <p className="text-sm text-slate-500 mb-4 max-w-md">
        {isChunkError
          ? 'The app has been updated. Please reload to get the latest version.'
          : error.message}
      </p>
      {isChunkError ? (
        <button
          onClick={() => window.location.reload()}
          className="flex items-center gap-2 px-4 py-2 bg-[#8e4585] text-white rounded-lg hover:bg-[#753a6f] transition-colors text-sm font-medium"
        >
          <RefreshCw size={14} />
          Reload Page
        </button>
      ) : (
        <button
          onClick={onReset}
          className="flex items-center gap-2 px-4 py-2 bg-[#8e4585] text-white rounded-lg hover:bg-[#753a6f] transition-colors text-sm font-medium"
        >
          <RefreshCw size={14} />
          Try Again
        </button>
      )}
    </div>
  );
}
